import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import CTA from "@/components/CTA";
import Marquee from "@/components/Marquee";
import SEO from "@/components/SEO";
import { RevealLines, FadeUp, ClipReveal } from "@/components/Reveal";
import { IMAGES, campuspathHref, TG_LOGO, OG_IMAGE_TESTGUARD } from "@/lib/constants";

const EASE = [0.16, 1, 0.3, 1];

const FEATURES = [
  { n: "01", title: "Browser Lockdown", line: "One secure window. No tabs, no copy-paste, no escape, until the exam ends." },
  { n: "02", title: "Device Monitoring", line: "See every testing device at a glance, with live status and instant flags." },
  { n: "03", title: "Proctor Dashboard", line: "Watch the whole room from a single screen and step in the moment something's off." },
  { n: "04", title: "Integrity Reports", line: "A clean, exportable record of every session, evidence you can stand behind." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-obsidian pt-36 pb-20 text-white md:pt-48 md:pb-28">
      <div className="pointer-events-none absolute inset-0">
        <motion.div style={{ scale }} className="h-full w-full">
          <img src={IMAGES.lock} alt="" className="h-full w-full object-cover opacity-60" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/35 via-obsidian/70 to-obsidian" />
      </div>

      <motion.div style={{ y }} className="relative mx-auto max-w-7xl px-6 md:px-8">
        <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-600 uppercase tracking-widest text-white/70 backdrop-blur">
          <span className="h-2 w-2 rounded-full bg-leaf" /> A CampusPath Product
        </div>
        <h1 className="max-w-4xl font-display text-[clamp(3rem,9vw,7rem)] font-900 leading-[0.9] tracking-tightest">
          <RevealLines lines={["Lock it down.", "Test with trust."]} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
          className="mt-8 max-w-xl text-lg leading-relaxed text-white/65"
        >
          TestGuard secures exams with device monitoring and full browser lockdown, a companion to
          CampusPath, or a standalone on its own.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.65 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <CTA to="/trial?product=testguard" variant="leafDark" dataTestId="tg-trial-btn">Start a free trial</CTA>
          <CTA to="/demo?product=testguard" variant="ghostDark" dataTestId="tg-demo-btn">Schedule a demo</CTA>
        </motion.div>
      </motion.div>
    </section>
  );
}

export default function TestGuard() {
  return (
    <main className="dark-scroll min-h-screen bg-obsidian text-white">
      <SEO
        title="TestGuard, Lock It Down. Test With Trust."
        description="TestGuard secures exams with device monitoring and full browser lockdown, a companion to CampusPath, or a standalone on its own. Proctor dashboard and integrity reports built in."
        canonical="https://testguard.org/"
        keywords="exam lockdown browser, online proctoring software, secure exam software, browser lockdown, device monitoring exams, TestGuard"
        image={OG_IMAGE_TESTGUARD}
        favicon={TG_LOGO}
      />
      <SiteNav dark />
      <Hero />
      <Marquee text="Secure Exams. Verified Integrity." dark />

      <section className="bg-obsidian py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-8">
          <FadeUp className="mb-16 max-w-2xl">
            <span className="text-xs font-600 uppercase tracking-widest text-leaf">How it protects</span>
            <h2 className="mt-4 font-display text-[clamp(2rem,4.5vw,3.4rem)] font-800 leading-[1.02] tracking-tightest">
              Four layers between students and shortcuts.
            </h2>
          </FadeUp>
          <div className="grid gap-6 md:grid-cols-2">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.n} delay={i * 0.08}>
                <div className="tracing-card group h-full rounded-2xl p-8 transition-transform duration-300 hover:-translate-y-1" data-testid={`tg-feature-${f.n}`}>
                  <div className="flex items-start justify-between">
                    <span className="numeral-outline-light font-display text-5xl font-900">{f.n}</span>
                    <span className="text-leaf opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-800">{f.title}</h3>
                  <p className="mt-3 leading-relaxed text-white/55">{f.line}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-obsidian pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 md:px-8">
          <div className="grid items-center gap-12 rounded-[2rem] border border-white/10 bg-surface-dark p-8 md:grid-cols-2 md:p-14">
            <div>
              <span className="text-xs font-600 uppercase tracking-widest text-leaf">Better together</span>
              <h2 className="mt-4 font-display text-[clamp(1.8rem,3.5vw,2.8rem)] font-800 leading-tight tracking-tightest">
                Pairs with CampusPath. Stands on its own.
              </h2>
              <p className="mt-5 leading-relaxed text-white/60">
                Roster, roles and student data flow straight from CampusPath, or run TestGuard
                independently for any exam, any device.
              </p>
              <div className="mt-8">
                <CTA href={campuspathHref()} variant="ghostDark" dataTestId="tg-campuspath-btn">Explore CampusPath</CTA>
              </div>
            </div>
            <ClipReveal className="overflow-hidden rounded-2xl">
              <img src={IMAGES.examLaptop} alt="Students taking a secured exam" className="h-72 w-full object-cover md:h-80" />
            </ClipReveal>
          </div>
        </div>
      </section>

      <SiteFooter dark />
    </main>
  );
}
